import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { getErrorNotification, setErrorNotification } from '~~/common/utils/helper';
import { DM_DELIVERY_COOLOMAT_LOCKER } from '~~/common/config/DeliveryMethod';
import { useContentsStore } from '~~/common/stores/contents';
import { useQueueStore } from '~~/common/stores/queue';
import { useOrderStore } from '~~/common/stores/order';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useAwCacheKeyStore } from '~~/common/stores/awCacheKey';
import { useProductsStore } from '~~/common/stores/products';
import { useProductStore } from '~~/common/stores/product';
import { useUserStore } from '~~/common/stores/user';
import { useCategoryStore } from '~~/common/stores/category';
import { useCheckoutStore } from '~~/common/stores/checkout';
import type { CheckoutData } from '~~/common/stores/checkout';
import { useAuthenticationStore } from '~~/common/stores/authentication.js';
import type { Schema } from '~~/common/types/api';
import type { DeliveryMethodsEnumType } from '~~/shop/app/components/Common/Modals/AwDeliveryMethodAndTimeframeModal/enum/deliveryMethodsEnum';
import type { AxiosError } from 'axios';

type DeliveryAreaType = Schema<'DeliveryAreaModel'>;
type DeliveryAreaRequestType = Schema<'DeliveryAreaModel.DeliveryAreaInputModel'>;
type DepartmentStoreType = Schema<'DepartmentStoreModel'>;
type DeliveryTimeframesType = Schema<'DeliveryTimeFrameModel'>;

interface PushMethodPayloadType extends DeliveryAreaRequestType {
  resolveOption?: string;
  nonfood?: boolean;
}


let globalDepartmentStores: Promise<DepartmentStoreType[]>;
export const useDeliveryStore = defineStore('delivery', () => {
  const nuxtApp = useNuxtApp();

  const checkoutStore = useCheckoutStore();
  const awCacheKeyStore = useAwCacheKeyStore();
  const contentsStore = useContentsStore();
  const userInterfaceStore = useUserInterfaceStore();
  const userStore = useUserStore();
  const productsStore = useProductsStore();
  const orderStore = useOrderStore();
  const productStore = useProductStore();
  const categoryStore = useCategoryStore();
  const queueStore = useQueueStore();
  const authenticationStore = useAuthenticationStore();

  const timeframes = ref<DeliveryTimeframesType[]>([]);
  const underApprovalTimeframe = ref<DeliveryTimeframesType | null>(null);
  const selectedTimeframe = ref<DeliveryTimeframesType | null>(null);
  const timeframeListSelectedTimeframe = ref<DeliveryTimeframesType | null>(null);
  const selectedDay = ref<Date | null>(null);
  const selectedDeliveryMethod = ref<DeliveryMethodsEnumType | null>(null);
  const timeZone = ref<string>('Europe/Budapest');
  const setup = ref<DeliveryAreaType | null>(null);
  const departmentStores = ref<DepartmentStoreType[]>([]);
  const foodDeliveryAvailable = ref(false);
  const expiredTimeframeVisible = ref<{
    expiredVisible: boolean | null;
    expireSoonVisible: boolean | null;
  }>({
    expiredVisible: null,
    expireSoonVisible: null,
  });
  const expiredColdParcelTimeframeVisible = ref<boolean | null>(null);
  const secondSeparatorWithoutMarginTop = ref<boolean | null>(null);

  // getters
  const bestTimeFrameOptions = computed(() => {
    if (!timeframes.value.length) {
      return [];
    }
    const activeTimeframes = timeframes.value.filter(timeframe => timeframe.status);

    if (!activeTimeframes.length) {
      return [];
    }

    const cheapestTimeframePrice = activeTimeframes
      .reduce((previous, current) => {
        return previous.price.grossDiscounted < current.price.grossDiscounted
          ? previous
          : current;
      })
      .price
      .grossDiscounted;

    const cheapestTimeframe = activeTimeframes
      .filter(current => {
        return current.price.grossDiscounted === cheapestTimeframePrice;
      })
      .sort((current, previous) => {
        return new Date(current.date).getTime() - new Date(previous.date).getTime();
      })[0];

    const earliestTimeframeFrom = activeTimeframes
      .reduce((previous, current) => {
        return previous.from < current.from ? previous : current;
      })
      .from;
    const earliestTimeframe = activeTimeframes
      .filter(timeframe => {
        return timeframe.from === earliestTimeframeFrom;
      })
      .sort((previous, current) => {
        return new Date(current.date).getTime() - new Date(previous.date).getTime();
      })[0];

    return {
      cheapestTimeframe,
      earliestTimeframe,
    };
  });
  const dayTimeframes = computed(() => (day: Date, locale: string) => {
    if (!(day instanceof Date)) {
      throw new TypeError('`day` should be type Date');
    }
    const sameDayConf = [locale, {
      timeZone: timeZone.value,
      year: '2-digit',
      month: '2-digit',
      day: '2-digit',
    }] as const;

    return timeframes.value.filter(({ from }) => {
      return day.toLocaleString(...sameDayConf as [string, Intl.DateTimeFormatOptions]) === (new Date(from)).toLocaleString(...sameDayConf);
    }).sort((a, b) => {
      if (a.from === b.from) {
        return 0;
      }
      return (a.from < b.from) ? -1 : 1;
    });
  });
  const hours = computed(() => {
    return (day: Date, locale: string) => {
      const config = [locale, {
        timeZone: timeZone.value,
        hour: '2-digit',
      }] as const;

      if (!(day instanceof Date)) {
        throw new TypeError('`day` should be type Date');
      }
      return dayTimeframes.value(day, locale)
        .map(({
          from,
          to,
        }) => [
          parseInt((new Date(from)).toLocaleString(...config), 10),
          parseInt((new Date(to)).toLocaleString(...config), 10),
        ]);

    };
  });
  const isSetupNonFood = computed(() => {
    const sdmt = setup.value?.deliveryMethodType;
    return sdmt && sdmt !== DM_DELIVERY_COOLOMAT_LOCKER;
  });
  const getPostCode = computed(() => {
    return setup.value?.postCode ? setup.value.postCode : '';
  });

  const coolomatId = computed(() => (setup.value?.deliveryMethodType === DM_DELIVERY_COOLOMAT_LOCKER && setup.value.lockerId) || null);

  // actions
  function selectTimeframeForApproval (timeframe: DeliveryTimeframesType | null) {
    underApprovalTimeframe.value = timeframe;
  }

  function clearTimeframeForApproval () {
    underApprovalTimeframe.value = null;
  }

  function selectTimeframe (tframe: DeliveryTimeframesType | null) {
    selectedTimeframe.value = tframe;
  }

  function setTimeframeListSelectedTimeframe (payload: DeliveryTimeframesType) {
    timeframeListSelectedTimeframe.value = payload;
  }

  function selectDay (day: Date | null) {
    selectedDay.value = day;
  }

  function clearDay () {
    selectedDay.value = null;
  }

  function setSelectedDeliveryMethod (payload: DeliveryMethodsEnumType) {
    selectedDeliveryMethod.value = payload;
  }

  function setDepartmentStores ({ newDepartmentStores }: { newDepartmentStores: DepartmentStoreType[] }) {
    departmentStores.value = newDepartmentStores;
  }

  function setExpiredVisible (expiredVisible: boolean | null) {
    expiredTimeframeVisible.value.expiredVisible = expiredVisible;
  }

  function setExpireSoonVisible (expireSoonVisible: boolean | null) {
    expiredTimeframeVisible.value.expireSoonVisible = expireSoonVisible;
  }

  function setExpiredColdParcelTimeframeVisible (expiredVisible: boolean | null) {
    expiredColdParcelTimeframeVisible.value = expiredVisible;
  }

  function setFoodDeliveryAvailable (payload: boolean) {
    foodDeliveryAvailable.value = payload;
  }

  function setSecondSeparatorWithoutMarginTop (payload: boolean | null) {
    secondSeparatorWithoutMarginTop.value = payload;
  }

  async function fetchMethod () {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const result = await $api.$get<DeliveryAreaType>('/delivery-area');
      setup.value = result;

      setFoodDeliveryAvailable(Boolean(result?.foodDeliveryAvailable));
      awCacheKeyStore.setCacheKeyLegacy(setup.value?.id ? `delivery_area_id=${setup.value.id}&delivery_area_type=${setup.value.type}&delivery_area_dmt=${setup.value.deliveryMethodType ?? null}` : '');
      // Only overwrite the segmentation code when /delivery-area actually returns one.
      // Otherwise we would wipe the value set by /cache-segmentation and trigger 409s on subsequent cached requests.
      if (setup.value?.cacheSegmentationCode) {
        awCacheKeyStore.setCacheKeySegmentation(`${setup.value.cacheSegmentationCode}`);
      }
    } catch (error) {
      $logger.error(error);
      await setErrorNotification({ nuxtApp, error });
    }

    try {
      const type = 'homepage';
      if (contentsStore.staticContent[type as keyof typeof contentsStore.staticContent]) {
        await contentsStore.fetchStaticContent({ type });
        const { backgroundRepeat, backgroundColor, media } = contentsStore.data;
        userInterfaceStore.setBackground({ backgroundRepeat, backgroundColor, media });
      }
    } catch (e) {
      const error = e as AxiosError<Schema<'ErrorModel'>>;
      if (error.response?.status !== 404) {
        $logger.error(error);
        await setErrorNotification({ nuxtApp, error });
      }
    }
  }

  async function pushMethod (payload: PushMethodPayloadType) {
    userInterfaceStore.startLoading({ id: `push-method-${payload.areaId}-${payload.type}` });

    if (import.meta.server) {
      return await pushMethodRequest(payload);
    } else {
      return await queueStore.add({
        action: pushMethodRequest,
        actionName: 'delivery/pushMethodRequest',
        payload,
        canStartSecondAction: true,
        canRunSimultaneous: false,
      });
    }
  }

  async function pushMethodRequest ({
    type,
    areaId,
    resolveOption,
    nonfood,
  }: PushMethodPayloadType) {
    const {
      $api,
    } = nuxtApp;

    await authenticationStore.reInitToken();

    try {
      let headerParameter = {};
      if (resolveOption) {
        headerParameter = {
          headers: {
            'X-Error-Resolve-Option': resolveOption,
          },
        };
      }
      await $api.$post('/delivery-area',
        {
          type,
          areaId,
        },
        headerParameter,
      );
      resetDataWhichDependsOnDeliveryArea();
      await fetchMethod();
      // The delivery area changed, so the companyNo may differ; re-fetch the segmentation data.
      await awCacheKeyStore.refreshCacheSegmentation();
      await refreshDataWhichDependsOnDeliveryArea({
        isNonfood: nonfood,
        isSkippableOrderRefresh: false,
      });
      return {
        status: true,
        error: null,
        notification: null,
      };
    } catch (error) {
      setFoodDeliveryAvailable(false);

      queueStore.removeAllElementFromQueue(false);

      const notification = getErrorNotification(nuxtApp, error, false, {
        storeAction: pushMethod,
        storeActionParameters: {
          type,
          areaId,
          resolveOption,
          nonfood,
        },
      });
      return {
        status: false,
        error,
        notification,
      };
    } finally {
      userInterfaceStore.endLoading({ id: `push-method-${areaId}-${type}` });
    }
  }

  function resetDataWhichDependsOnDeliveryArea () {
    selectedTimeframe.value = null;
    clearTimeframeForApproval();
    clearDay();
    setExpiredVisible(null);
    setExpireSoonVisible(null);

    productsStore.resetFilterBy();
    productsStore.throttledRefresh();
  }

  async function refreshDataWhichDependsOnDeliveryArea ({
    isNonfood,
    isSkippableOrderRefresh = false,
  }: {
    isNonfood?: boolean;
    isSkippableOrderRefresh: boolean;
  }) {
    const {
      $cookies,
    } = nuxtApp;
    const product = productStore.data;
    if (product?.id) {
      productStore.fetchMainProduct({
        identifierType: 'id',
        id: product.id,
        params: undefined,
        specialErrorMessage: undefined,
      });
    }
    if (!isNonfood) {
      await fetchTimeframes();
    }
    const orderResult = (isSkippableOrderRefresh
      ? false
      : await orderStore.fetchOrder({
        id: 'current',
        fetchNonfoodCheckoutIfNFCartNotEmpty: !isNonfood,
        fetchFoodCheckout: false,
        canStartSecondAction: true,
        canRunSimultaneous: true,
      })
    );
    const fetchOrderErrorStatus = ((typeof orderResult === 'object' && orderResult !== null && 'fetchOrderErrorStatus' in orderResult)
      ? orderResult.fetchOrderErrorStatus
      : false
    );
    if (!fetchOrderErrorStatus) {
      await categoryStore.fetchCategories();
      // get shopping list, when user is logged in
      const shoppingLists = userStore.data?.shoppingLists;
      if (userStore.isEcomUser && shoppingLists) {
        await userStore.fetchShoppingLists({
          itemsPerPage: userStore.shoppingListsPagination.itemsPerPage * userStore.shoppingListsPagination.page,
          page: 1,
          mergeOldAndNewItems: false,
          updatePagination: false,
        });
      }

      const cookieKey = 'selected_postcode';
      if (setup.value) {
        $cookies.set(cookieKey, JSON.stringify({
          type: setup.value.type,
          areaId: parseInt(setup.value.id, 10),
          deliveryMethodType: setup.value.deliveryMethodType,
        }), {
          path: '/',
          maxAge: 7 * 24 * 60 * 60,
        });
      } else {
        $cookies.remove(cookieKey, { path: '/' });
      }
    }
  }

  async function fetchDepartmentStores () {
    const { $api } = nuxtApp;
    try {
      globalDepartmentStores = $api.$get<DepartmentStoreType[]>('/department_stores');

      setDepartmentStores({ newDepartmentStores: await globalDepartmentStores });
    } catch (error) {
      setErrorNotification({ nuxtApp, error });
    }
    await globalDepartmentStores;
  }

  async function fetchTimeframes (params?: { coolomatId?: string }) {
    const {
      $api,
      $logger,
    } = nuxtApp;

    try {
      await Promise.all([
        $api.get<DeliveryTimeframesType[]>(`delivery_time_frames${params?.coolomatId ? '?coolomatId=' + params?.coolomatId : ''}`)
          .then(({ data }) => {
            timeframes.value = data;
          }),
        $api.get<CheckoutData>('checkouts/FOOD')
          .then(({ data }) => {
            checkoutStore.setCheckout({
              checkout: data,
              type: 'FOOD',
            });
          })
          .catch((e) => {
            $logger.error(e);
          }),
      ]);

      const foodCheckout = checkoutStore.getFoodCheckout;
      const newSelectedTimeframe = timeframes.value.find(({ id }) => id === foodCheckout?.reservation?.id) || null;
      if (newSelectedTimeframe) {
        const newSelectedDay = newSelectedTimeframe.date;
        selectTimeframe(newSelectedTimeframe);
        selectTimeframeForApproval(newSelectedTimeframe);
        selectDay(new Date(newSelectedDay));
      } else {
        selectTimeframe(null);
        selectTimeframeForApproval(null);
        selectDay(null);
      }
    } catch (error) {
      setErrorNotification({ nuxtApp, error });
    }
  }

  return {
    timeframes,
    underApprovalTimeframe,
    selectedTimeframe,
    timeframeListSelectedTimeframe,
    selectedDay,
    selectedDeliveryMethod,
    timeZone,
    setup,
    departmentStores,
    foodDeliveryAvailable,
    expiredTimeframeVisible,
    expiredColdParcelTimeframeVisible,
    secondSeparatorWithoutMarginTop,
    bestTimeFrameOptions,
    dayTimeframes,
    hours,
    isSetupNonFood,
    getPostCode,
    coolomatId,
    selectTimeframeForApproval,
    clearTimeframeForApproval,
    selectTimeframe,
    setTimeframeListSelectedTimeframe,
    selectDay,
    clearDay,
    setSelectedDeliveryMethod,
    setDepartmentStores,
    setExpiredVisible,
    setExpireSoonVisible,
    setExpiredColdParcelTimeframeVisible,
    setFoodDeliveryAvailable,
    setSecondSeparatorWithoutMarginTop,
    fetchMethod,
    pushMethod,
    pushMethodRequest,
    fetchDepartmentStores,
    fetchTimeframes,
    resetDataWhichDependsOnDeliveryArea,
    refreshDataWhichDependsOnDeliveryArea,
  };
});
